import type { CellarLocation, CellarPlaceRecord } from "../types";

export type CellarOccupancyGroup = {
  id: string;
  name: string;
  locations: CellarLocation[];
  stock: number;
  capacity: number | null;
  free: number | null;
  overflow: number;
  unassigned: boolean;
};

const UNASSIGNED_OCCUPANCY_ID = "__unassigned_occupancy__";

function groupMetrics(
  id: string,
  name: string,
  locations: CellarLocation[],
  unassigned = false,
): CellarOccupancyGroup {
  const stock = locations.reduce((total, location) => total + location.stock, 0);
  const capacity =
    locations.length > 0 &&
    locations.every((location) => location.capacite_totale != null)
      ? locations.reduce(
          (total, location) => total + (location.capacite_totale ?? 0),
          0,
        )
      : null;
  const free =
    capacity == null
      ? null
      : locations.reduce(
          (total, location) =>
            total +
            Math.max((location.capacite_totale ?? 0) - location.stock, 0),
          0,
        );
  const overflow =
    capacity == null
      ? 0
      : locations.reduce(
          (total, location) =>
            total +
            Math.max(location.stock - (location.capacite_totale ?? 0), 0),
          0,
        );

  return {
    id,
    name,
    locations,
    stock,
    capacity,
    free,
    overflow,
    unassigned,
  };
}

export function buildCellarOccupancy(
  places: readonly CellarPlaceRecord[],
  locations: readonly CellarLocation[],
): CellarOccupancyGroup[] {
  const knownPlaceIds = new Set(places.map((place) => place.id));
  const groups = places.map((place) =>
    groupMetrics(
      place.id,
      place.nom,
      locations.filter((location) => location.lieu_id === place.id),
    ),
  );
  const unassigned = locations.filter(
    (location) => !location.lieu_id || !knownPlaceIds.has(location.lieu_id),
  );

  if (unassigned.length > 0) {
    groups.push(
      groupMetrics(
        UNASSIGNED_OCCUPANCY_ID,
        "Lieu à préciser",
        unassigned,
        true,
      ),
    );
  }

  return groups;
}
